import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import './AdminProductForm.css';

const SIZE_LABELS = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const CATEGORIES = ['T-Shirt', 'Hoodie', 'Sweatshirt', 'Bag', 'Watch', 'Moneybag', 'Saree', 'Tops', 'Three Piece'];

const defaultSizes = SIZE_LABELS.map(label => ({ label, available: false, preOrder: false }));

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: '',
    category: 'T-Shirt',
    description: '',
    price: '',
    discountedPrice: '',
    salePrice: '',
    featured: false,
    youtubeUrl: '',
  });

  const [sizes, setSizes] = useState(defaultSizes);
  const [noSize, setNoSize] = useState(false);
  const [noSizeOpts, setNoSizeOpts] = useState({ available: false, preOrder: false });
  const [thumbnail, setThumbnail] = useState('');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);

  const thumbRef = useRef();
  const mediaRef = useRef();

  // Load product if editing
  useEffect(() => {
    if (isEdit) {
      api.get(`/products/${id}`).then(({ data }) => {
        setForm({
          name: data.name,
          category: data.category,
          description: data.description,
          price: data.price,
          discountedPrice: data.discountedPrice || '',
          salePrice: data.salePrice || '',
          featured: data.featured || false,
          youtubeUrl: data.youtubeUrl || '',
        });
        setThumbnail(data.thumbnail || '');
        setImages(data.images || []);
        setVideos(data.videos || []);
        // Check if this is a no-size product
        const isNoSize = data.sizes?.length === 1 && data.sizes[0].label === 'NO_SIZE';
        if (isNoSize) {
          setNoSize(true);
          setNoSizeOpts({ available: data.sizes[0].available, preOrder: data.sizes[0].preOrder });
        } else {
          setNoSize(false);
          const mergedSizes = SIZE_LABELS.map(label => {
            const found = data.sizes?.find(s => s.label === label);
            return found || { label, available: false, preOrder: false };
          });
          setSizes(mergedSizes);
        }
      });
    }
  }, [id, isEdit]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSizeToggle = (label, field) => {
    setSizes(prev => prev.map(s =>
      s.label === label ? { ...s, [field]: !s[field] } : s
    ));
  };

  // Upload thumbnail
  const uploadThumbnail = async () => {
    if (!thumbnailFile) return thumbnail;
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading thumbnail...');
    try {
      const formData = new FormData();
      formData.append('files', thumbnailFile);
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(pct);
        },
      });
      const url = data.files[0].url;
      setThumbnail(url);
      setThumbnailFile(null);
      setUploadProgress(100);
      setUploadStatus('Thumbnail uploaded ✓');
      return url;
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Thumbnail upload failed';
      toast.error(msg);
      throw new Error(msg);
    } finally {
      setUploading(false);
    }
  };

  // Upload media (images + videos)
  const uploadMedia = async () => {
    if (mediaFiles.length === 0) return { imgs: images, vids: videos };
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(`Uploading ${mediaFiles.length} file(s)...`);
    try {
      const formData = new FormData();
      mediaFiles.forEach(f => formData.append('files', f));
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(pct);
            setUploadStatus(`Uploading ${mediaFiles.length} file(s)... ${pct}%`);
          }
        },
      });
      const newImgs = data.files.filter(f => f.type === 'image').map(f => f.url);
      const newVids = data.files.filter(f => f.type === 'video').map(f => f.url);
      const mergedImgs = [...images, ...newImgs];
      const mergedVids = [...videos, ...newVids];
      setImages(mergedImgs);
      setVideos(mergedVids);
      setMediaFiles([]);
      setUploadProgress(100);
      setUploadStatus(`${data.files.length} file(s) uploaded ✓`);
      return { imgs: mergedImgs, vids: mergedVids };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Media upload failed';
      toast.error(msg);
      throw new Error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.category || !form.description || !form.price) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      let thumbUrl = thumbnail;
      let imgUrls = images;
      let vidUrls = videos;

      if (thumbnailFile) thumbUrl = await uploadThumbnail();
      if (mediaFiles.length > 0) {
        const { imgs, vids } = await uploadMedia();
        imgUrls = imgs;
        vidUrls = vids;
      }

      if (!thumbUrl) { toast.error('Please upload a thumbnail image'); setSaving(false); return; }

      const payload = {
        ...form,
        price: Number(form.price),
        discountedPrice: form.discountedPrice ? Number(form.discountedPrice) : undefined,
        salePrice: form.salePrice ? Number(form.salePrice) : undefined,
        thumbnail: thumbUrl,
        images: imgUrls,
        videos: vidUrls,
        youtubeUrl: form.youtubeUrl.trim(),
        sizes: noSize
          ? [{ label: 'NO_SIZE', available: noSizeOpts.available, preOrder: noSizeOpts.preOrder }]
          : sizes,
      };

      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success('Product updated!');
      } else {
        await api.post('/products', payload);
        toast.success('Product created!');
      }
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeImage = (url) => setImages(prev => prev.filter(i => i !== url));
  const removeVideo = (url) => setVideos(prev => prev.filter(v => v !== url));

  // Extract YouTube video ID from any YouTube URL format
  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };

  const youtubeId = getYouTubeId(form.youtubeUrl);

  return (
    <div className="product-form-page">
      <div className="pf-header">
        <div>
          <div className="admin-page-title">{isEdit ? 'Edit Product' : 'Add New Product'}</div>
          <p className="admin-page-sub">{isEdit ? 'Update product details' : 'Fill in the details below'}</p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/admin/products')}>
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="pf-grid">
        {/* Left Column */}
        <div className="pf-main">
          {/* Basic Info */}
          <div className="pf-card card">
            <h3>📋 Basic Information</h3>

            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input className="form-input" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Classic Black Hoodie" />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select className="form-input" name="category" value={form.category} onChange={handleChange}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} style={{ marginRight: 8 }} />
                  Mark as Featured
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea className="form-input" name="description" value={form.description} onChange={handleChange} required rows={5} placeholder="Describe the product in detail..." style={{ resize: 'vertical' }} />
            </div>
          </div>

          {/* Pricing */}
          <div className="pf-card card">
            <h3>💰 Pricing</h3>
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">Original Price (৳) *</label>
                <input className="form-input" name="price" type="number" value={form.price} onChange={handleChange} required min={0} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Discounted Price (৳)</label>
                <input className="form-input" name="discountedPrice" type="number" value={form.discountedPrice} onChange={handleChange} min={0} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label className="form-label">Sale Price (৳)</label>
                <input className="form-input" name="salePrice" type="number" value={form.salePrice} onChange={handleChange} min={0} placeholder="Optional" />
              </div>
            </div>
            {form.price && form.salePrice && (
              <div className="price-preview">
                <span>Original: <strong>৳{form.price}</strong></span>
                <span>→ Sale: <strong className="sale-highlight">৳{form.salePrice}</strong></span>
                <span className="discount-pct">
                  ({Math.round(((form.price - form.salePrice) / form.price) * 100)}% off)
                </span>
              </div>
            )}
          </div>

          {/* Sizes */}
          <div className="pf-card card">
            <h3>📐 Sizes & Availability</h3>

            {/* No Size toggle */}
            <label className="nosize-toggle">
              <input
                type="checkbox"
                checked={noSize}
                onChange={e => setNoSize(e.target.checked)}
              />
              <span className="nosize-toggle-box">
                <span className="nosize-check">{noSize ? '✓' : ''}</span>
              </span>
              <span className="nosize-label">
                This product has <strong>No Size</strong>
                <small>(e.g. Bag, Watch, Saree, Moneybag)</small>
              </span>
            </label>

            {noSize ? (
              /* No-size: just available / pre-order */
              <div className="nosize-opts">
                <p className="nosize-opts-note">Choose how customers can order this product:</p>
                <div className="nosize-opts-grid">
                  <label className={`nosize-opt-card ${noSizeOpts.available ? 'active-avail' : ''}`}>
                    <input
                      type="checkbox"
                      checked={noSizeOpts.available}
                      onChange={e => setNoSizeOpts(prev => ({ ...prev, available: e.target.checked }))}
                    />
                    <span className="nso-icon">✅</span>
                    <div>
                      <strong>Available</strong>
                      <small>In stock, ready to order</small>
                    </div>
                  </label>
                  <label className={`nosize-opt-card ${noSizeOpts.preOrder ? 'active-pre' : ''}`}>
                    <input
                      type="checkbox"
                      checked={noSizeOpts.preOrder}
                      onChange={e => setNoSizeOpts(prev => ({ ...prev, preOrder: e.target.checked }))}
                    />
                    <span className="nso-icon">🔖</span>
                    <div>
                      <strong>Pre-Order</strong>
                      <small>Advance booking available</small>
                    </div>
                  </label>
                </div>
              </div>
            ) : (
              /* Normal sized product */
              <>
                <p className="size-instruction">Check "Available" for in-stock sizes. Check "Pre-Order" for sizes available for advance booking.</p>
                <div className="sizes-table">
                  <div className="sizes-table-head">
                    <span>Size</span>
                    <span>Available</span>
                    <span>Pre-Order</span>
                  </div>
                  {sizes.map(size => (
                    <div key={size.label} className="size-row">
                      <span className="size-row-label">{size.label}</span>
                      <label className="check-wrap">
                        <input
                          type="checkbox"
                          checked={size.available}
                          onChange={() => handleSizeToggle(size.label, 'available')}
                        />
                        <span className="custom-check available">
                          {size.available ? '✓' : ''}
                        </span>
                      </label>
                      <label className="check-wrap">
                        <input
                          type="checkbox"
                          checked={size.preOrder}
                          onChange={() => handleSizeToggle(size.label, 'preOrder')}
                        />
                        <span className="custom-check preorder">
                          {size.preOrder ? '✓' : ''}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="pf-sidebar">
          {/* Thumbnail */}
          <div className="pf-card card">
            <h3>🖼️ Thumbnail Image *</h3>
            <p className="upload-note">This is the main product image shown in listings.</p>

            {thumbnail ? (
              <div className="thumb-preview">
                <img src={thumbnail} alt="Thumbnail" className="thumb-prev-img" />
                <button
                  type="button"
                  className="remove-thumb-btn"
                  onClick={() => { setThumbnail(''); setThumbnailFile(null); }}
                >
                  ✕ Remove
                </button>
              </div>
            ) : thumbnailFile ? (
              <div className="thumb-preview">
                <img src={URL.createObjectURL(thumbnailFile)} alt="Preview" className="thumb-prev-img" />
                <button type="button" className="remove-thumb-btn" onClick={() => setThumbnailFile(null)}>✕ Remove</button>
              </div>
            ) : (
              <div className="upload-area" onClick={() => thumbRef.current.click()}>
                <div className="upload-icon">📷</div>
                <p>Click to upload thumbnail</p>
                <span>PNG, JPG up to 10MB</span>
              </div>
            )}
            <input ref={thumbRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) { setThumbnailFile(e.target.files[0]); setThumbnail(''); } }}
            />
            {!thumbnail && !thumbnailFile && (
              <button type="button" className="btn btn-outline upload-pick-btn" onClick={() => thumbRef.current.click()}>
                📷 Choose Image
              </button>
            )}
          </div>

          {/* Media Gallery */}
          <div className="pf-card card">
            <h3>📸 Product Images & Videos</h3>
            <p className="upload-note">Upload multiple images and videos. Drag and drop or click to select.</p>

            <div className="media-grid">
              {images.map((img, i) => (
                <div key={i} className="media-item">
                  <img src={img} alt="" className="media-thumb" />
                  <button type="button" className="media-remove" onClick={() => removeImage(img)}>✕</button>
                  <span className="media-type-badge">IMG</span>
                </div>
              ))}
              {videos.map((vid, i) => (
                <div key={i} className="media-item video-item">
                  <div className="video-placeholder">▶</div>
                  <button type="button" className="media-remove" onClick={() => removeVideo(vid)}>✕</button>
                  <span className="media-type-badge">VID</span>
                </div>
              ))}
              {mediaFiles.map((file, i) => (
                <div key={`new-${i}`} className="media-item new-item">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="" className="media-thumb" />
                  ) : (
                    <div className="video-placeholder">▶</div>
                  )}
                  <button
                    type="button"
                    className="media-remove"
                    onClick={() => setMediaFiles(prev => prev.filter((_, idx) => idx !== i))}
                  >✕</button>
                  <span className="media-type-badge pending">NEW</span>
                </div>
              ))}
              <div
                className="media-add"
                onClick={() => mediaRef.current.click()}
              >
                <span>+</span>
                <span>Add</span>
              </div>
            </div>

            <input
              ref={mediaRef}
              type="file"
              multiple
              accept="image/*,video/*"
              style={{ display: 'none' }}
              onChange={e => {
                const files = Array.from(e.target.files);
                setMediaFiles(prev => [...prev, ...files]);
                e.target.value = '';
              }}
            />
          </div>

          {/* YouTube Video */}
          <div className="pf-card card">
            <h3>▶️ YouTube Video <span className="optional-tag">Optional</span></h3>
            <p className="upload-note">Paste a YouTube link. Customers will see an embedded video on the product page.</p>
            <div className="form-group" style={{ marginBottom: youtubeId ? 16 : 0 }}>
              <input
                className="form-input"
                name="youtubeUrl"
                value={form.youtubeUrl}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            {form.youtubeUrl && !youtubeId && (
              <p className="yt-invalid">⚠️ Invalid YouTube URL. Paste a valid youtube.com or youtu.be link.</p>
            )}
            {youtubeId && (
              <div className="yt-preview-wrap">
                <div className="yt-preview-label">Preview</div>
                <div className="yt-iframe-box">
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    title="YouTube preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="yt-iframe"
                  />
                </div>
                <button
                  type="button"
                  className="yt-remove-btn"
                  onClick={() => setForm(f => ({ ...f, youtubeUrl: '' }))}
                >
                  ✕ Remove Video
                </button>
              </div>
            )}
          </div>

          {/* Save */}
          <div className="pf-card card">
            {uploading && (
              <div className="upload-progress">
                <div className="upload-progress-bar-wrap">
                  <div
                    className="upload-progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p>{uploadStatus || 'Uploading to Cloudinary...'} {uploadProgress > 0 && uploadProgress < 100 ? `${uploadProgress}%` : ''}</p>
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary save-btn"
              disabled={saving || uploading}
            >
              {saving || uploading ? '⏳ Saving...' : isEdit ? '💾 Update Product' : '🚀 Publish Product'}
            </button>
            <button
              type="button"
              className="btn btn-outline save-btn"
              onClick={() => navigate('/admin/products')}
              style={{ marginTop: 10 }}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
